import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote, CheckCircle2 } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { cn } from '@/lib/utils';

const testimonials = [
  {
    name: 'Scott Waller',
    business: 'Scott Waller Plumbing & Heating',
    location: 'Kenfig Hill',
    rating: 5,
    quote: "Excellent service, my website looks brilliant even better than what I expected, great to work with 100% recommend",
  },
  {
    name: 'Lincoln Buckley',
    business: 'Neath Business Owner',
    location: 'Neath',
    rating: 5,
    quote: "The best I have ever worked with. SmartSite Solutions took away all the stress as I'm hands on, not very good with tech. Thanks to James I can sit back and relax knowing my website is in the best of hands. Thank you again 10/10",
  },
  {
    name: 'Laura Scott',
    business: 'North Cornelly Business Owner',
    location: 'North Cornelly',
    rating: 5,
    quote: "Outstanding Web Design Services! I recently worked with SmartSite Solutions to redesign my business website, and I couldn't be happier with the results. From the initial consultation to the final launch, their team was professional, responsive, and incredibly creative. They took the time to understand my brand, goals, and target audience, and delivered a design that not only looks stunning but is also highly functional and user-friendly. The site loads fast, is fully responsive on all devices, and ranks better on Google thanks to their SEO-friendly development practices. Communication throughout the process was excellent. If you're looking for a web design company that combines technical skill with creative vision and great customer service, I highly recommend SmartSite Solutions.",
  },
  {
    name: 'Gareth Lewis',
    business: 'North Cornelly Business Owner',
    location: 'North Cornelly',
    rating: 5,
    quote: "Professional, Affordable and a pleasure to work with. Great customer support.",
  },
  {
    name: 'Simon Davies',
    business: 'Barry Business Owner',
    location: 'Barry',
    rating: 5,
    quote: "James is a great guy to deal with. Personable, professional and always goes the extra mile to deliver a great service.",
  },
  {
    name: 'Luke Williams',
    business: 'All Inclusive Hols',
    location: 'South Wales',
    rating: 5,
    quote: "Having known James and Mike for a number of years, I would highly recommend their services. Both deliver a professional approach to project development, have meticulous attention to detail and have been very supportive throughout the customer journey.",
  },
];

export function TestimonialsSection() {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'start',
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
      duration: 20
    },
    [Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true })]
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const toggleExpand = (index: number) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const needsTruncation = (quote: string) => quote.length > 180;

  return (
    <section className="py-16 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 mesh-gradient opacity-30" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-muted-foreground mb-6">
            Success Stories
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Real Results from{' '}
            <span className="text-gradient">Real Businesses</span>
          </h2>
        </motion.div>
        
        {/* Testimonial Carousel */}
        <div className="max-w-6xl mx-auto">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex-[0_0_100%] md:flex-[0_0_50%] min-w-0 px-2 md:px-3"
                >
                  <div 
                    className={cn(
                      "h-full flex flex-col bg-white/5 backdrop-blur-xl border rounded-3xl p-6 md:p-8 transition-all duration-300 group",
                      selectedIndex === index 
                        ? "border-cyan-500/40 shadow-[0_0_40px_rgba(34,211,238,0.15)]" 
                        : "border-white/10 hover:border-cyan-500/30 hover:shadow-[0_0_30px_rgba(34,211,238,0.1)]"
                    )}
                  >
                    {/* Quote Icon + Rating Row */}
                    <div className="flex items-start justify-between mb-4">
                      <Quote className="w-10 h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.4)] flex-shrink-0" />
                      <div className="flex gap-0.5">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                    
                    {/* Quote container with flex-1 to push author to bottom */}
                    <div className="flex-1 flex flex-col">
                      <blockquote 
                        className={cn(
                          "text-base md:text-lg font-medium leading-relaxed mb-3 text-slate-200 transition-all duration-300",
                          !expandedCards.has(index) && needsTruncation(testimonial.quote) && "line-clamp-3"
                        )}
                      >
                        "{testimonial.quote}"
                      </blockquote>
                      
                      {/* Read more button wrapper with fixed height for alignment */}
                      <div className="h-6 mb-2">
                        {needsTruncation(testimonial.quote) && (
                          <button 
                            onClick={() => toggleExpand(index)}
                            className="text-cyan-400 text-sm font-medium hover:text-cyan-300 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-2 focus:ring-offset-slate-900 rounded"
                          >
                            {expandedCards.has(index) ? 'Read less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Author - always at bottom */}
                    <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                      {/* Google Logo */}
                      <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shadow-lg flex-shrink-0">
                        <svg viewBox="0 0 24 24" className="w-6 h-6">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-semibold text-foreground text-sm truncate">{testimonial.name}</p>
                          <div className="flex items-center gap-0.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-400 fill-green-400/20" />
                            <span className="text-[10px] text-green-400 font-medium">Verified</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{testimonial.business}</p>
                        <p className="text-[10px] text-cyan-400">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={scrollPrev}
              aria-label="Previous testimonial"
              className="w-12 h-12 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-cyan-500/30 transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            {/* Progress bar */}
            <div className="w-32 sm:w-40 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"
                initial={false}
                animate={{ width: `${((selectedIndex + 1) / testimonials.length) * 100}%` }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            </div>
            
            <button
              onClick={scrollNext}
              aria-label="Next testimonial"
              className="w-12 h-12 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-cyan-500/30 transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
